import React, { useState, useEffect, useMemo } from "react";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import { isRepeatableFieldVisible } from "../../../utils/repeatableFieldVisible";
import { isSchemaSectionShown } from "../../../utils/schemaSectionVisibility";
import { cn } from "../../../utils/cn";
import {
  cpfDigitsOnly,
  maskCpf,
  cnpjDigitsOnly,
  maskCnpj,
  phoneDigitsOnly,
  maskPhoneBr,
  normalizeBrlCurrencyInput,
  maskBrlCurrencyDisplay,
  normalizeDecimalBrInput,
  maskDecimalBrDisplay,
} from "../../../utils/masks";
import { brlToExtenso } from "../../../utils/brlExtenso";
import { detectExtensoPairsInFields } from "../../../utils/extensoFieldRelations";
import { conditionalOptionLabels } from "../../../documents/dynamicTextRegistry";
import AddCustomersFromListModal from "./AddCustomersFromListModal";

/**
 * Convert a conditional option key (`aprazo`, `sala_comercial`, `sem_exclusividade`,
 * ...) into a readable label when no explicit mapping is registered. The canonical
 * value sent to the backend stays untouched — only the dropdown label is humanized.
 */
function humanizeOptionKey(key) {
  const str = String(key ?? "").trim();
  if (str === "") return "";
  const withSpaces = str.replace(/[_-]+/g, " ").trim();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

/** Resolve the display label for a conditional-section option key. */
function getConditionalOptionLabel(section, key) {
  const override = section?.optionLabels?.[key];
  if (typeof override === "string" && override.length > 0) return override;
  const registered = conditionalOptionLabels[key];
  if (typeof registered === "string" && registered.length > 0) return registered;
  return humanizeOptionKey(key);
}

const EXTENSO_LOCKED_INPUT_CLASS =
  "bg-muted/60 text-muted-foreground cursor-not-allowed";
const EXTENSO_AUTO_DESCRIPTION =
  "Preenchido automaticamente a partir do valor em reais.";

/**
 * Read-only props applied to an `<Input>` derived from a BRL `_EXTENSO` source.
 * Caller is responsible for merging `className` (kept here so it can be combined
 * with the existing per-row "mb-4" or similar spacing).
 */
function extensoInputProps(isLocked, baseDescription) {
  if (!isLocked) {
    return baseDescription ? { description: baseDescription } : {};
  }
  return {
    readOnly: true,
    tabIndex: -1,
    "aria-readonly": "true",
    description: baseDescription || EXTENSO_AUTO_DESCRIPTION,
  };
}

function resolveEffectiveMask(field, context = {}) {
  if (field.maskDependsOn && field.maskByValue) {
    const key = context[field.maskDependsOn] || field.maskByValue.cpf || "cpf";
    return field.maskByValue[key] || field.mask;
  }
  return field.mask;
}

function withResolvedMask(field, context = {}) {
  const mask = resolveEffectiveMask(field, context);
  return mask ? { ...field, mask } : field;
}

function maskDisplayValue(field, raw, context = {}) {
  const mask = resolveEffectiveMask(field, context);
  if (mask === "cpf") return maskCpf(raw);
  if (mask === "cnpj") return maskCnpj(raw);
  if (mask === "phone") return maskPhoneBr(raw);
  if (mask === "brl_currency") return maskBrlCurrencyDisplay(raw);
  if (mask === "decimal_br") return maskDecimalBrDisplay(raw);
  return raw ?? "";
}

function normalizeMaskedInput(field, input, context = {}) {
  const mask = resolveEffectiveMask(field, context);
  if (mask === "cpf") return cpfDigitsOnly(input);
  if (mask === "cnpj") return cnpjDigitsOnly(input);
  if (mask === "phone") return phoneDigitsOnly(input);
  if (mask === "brl_currency") return normalizeBrlCurrencyInput(input);
  if (mask === "decimal_br") return normalizeDecimalBrInput(input);
  return input;
}

function numericMaskProps(field, context = {}) {
  const mask = resolveEffectiveMask(field, context);
  const masked =
    mask === "cpf" ||
    mask === "cnpj" ||
    mask === "phone" ||
    mask === "brl_currency" ||
    mask === "decimal_br";
  const decimal = mask === "brl_currency" || mask === "decimal_br";
  return {
    inputMode: decimal ? "decimal" : masked ? "numeric" : undefined,
    autoComplete: masked ? "off" : undefined,
  };
}

const DynamicContractForm = ({
  fields,
  /** Full schema for customer-import modal mapping; defaults to `fields`. */
  fullFormSchema,
  formData,
  setFormData,
  errors,
  onFieldFocus,
  onFieldBlur,
  customerImport,
  onAppendRepeatableRows,
  /** `accordion`: collapsible sections (default). `stepper`: one wizard screen, static section titles. */
  layoutMode = "accordion",
}) => {
  const schemaForCustomerModal = fullFormSchema ?? fields;
  // ---------------------------------------------------------------------------
  // Normalize schema (keeps backward compatibility)
  // ---------------------------------------------------------------------------
  const normalizedSections = Array.isArray(fields)
    ? fields[0]?.fields || fields[0]?.repeatable || fields[0]?.type === "conditional"
      ? fields
      : [
        {
          sectionId: "default",
          sectionLabel: "Dados do Documento",
          fields,
        },
      ]
    : [];

  const schemaForVisibility = fullFormSchema ?? fields;
  const visibleSections = normalizedSections.filter((section) => {
    const fromSchema =
      schemaForVisibility.find((s) => s.sectionId === section.sectionId) ??
      section;
    return isSchemaSectionShown(fromSchema, formData);
  });

  // ---------------------------------------------------------------------------
  // BRL "extenso" auto-fill
  // ---------------------------------------------------------------------------
  /** Schema used to discover extenso↔brl_currency relations (independent of step). */
  const schemaForExtenso = fullFormSchema ?? fields;

  /** Set of every field id (across the full schema) that must be rendered as
   *  a read-only "extenso" mirror of a brl_currency source. */
  const extensoLockedFieldIds = useMemo(() => {
    const ids = new Set();
    if (!Array.isArray(schemaForExtenso)) return ids;

    for (const section of schemaForExtenso) {
      if (section?.repeatable && Array.isArray(section.fields)) {
        detectExtensoPairsInFields(section.fields).forEach((_src, ext) =>
          ids.add(ext)
        );
      } else if (section?.type === "conditional" && section.options) {
        for (const opt of Object.values(section.options)) {
          if (Array.isArray(opt)) {
            detectExtensoPairsInFields(opt).forEach((_src, ext) => ids.add(ext));
          }
        }
      } else if (Array.isArray(section?.fields)) {
        detectExtensoPairsInFields(section.fields).forEach((_src, ext) =>
          ids.add(ext)
        );
      }
    }
    return ids;
  }, [schemaForExtenso]);

  /** Keep every detected extenso field in sync with its brl_currency source.
   *  Runs after every formData mutation (idempotent: only writes when the
   *  expected text differs from what's already stored, so it cannot loop). */
  useEffect(() => {
    if (!Array.isArray(schemaForExtenso)) return;
    if (extensoLockedFieldIds.size === 0) return;

    let next = null;
    const stage = (mutator) => {
      if (!next) next = { ...formData };
      mutator(next);
    };

    for (const section of schemaForExtenso) {
      if (section?.repeatable && Array.isArray(section.fields)) {
        const pairs = detectExtensoPairsInFields(section.fields);
        if (pairs.size === 0) continue;

        const items = formData[section.groupKey];
        if (!Array.isArray(items) || items.length === 0) continue;

        let groupChanged = false;
        const newItems = items.map((item) => {
          let row = item;
          pairs.forEach((srcId, extId) => {
            const expected = brlToExtenso(item?.[srcId]);
            const current = item?.[extId] ?? "";
            if (current !== expected) {
              row = { ...row, [extId]: expected };
            }
          });
          if (row !== item) groupChanged = true;
          return row;
        });

        if (groupChanged) {
          stage((draft) => {
            draft[section.groupKey] = newItems;
          });
        }
      } else if (section?.type === "conditional" && section.options) {
        const groupVal = formData[section.groupKey];
        const selectedType = groupVal?.type;
        if (!selectedType) continue;

        const optionKey = Object.keys(section.options).find(
          (k) => String(k).toLowerCase() === String(selectedType).toLowerCase()
        );
        if (!optionKey) continue;

        const optionFields = section.options[optionKey];
        const pairs = detectExtensoPairsInFields(optionFields);
        if (pairs.size === 0) continue;

        const currentFields = groupVal?.fields ?? {};
        let newFields = currentFields;
        pairs.forEach((srcId, extId) => {
          const expected = brlToExtenso(currentFields[srcId]);
          const current = currentFields[extId] ?? "";
          if (current !== expected) {
            newFields = { ...newFields, [extId]: expected };
          }
        });

        if (newFields !== currentFields) {
          stage((draft) => {
            draft[section.groupKey] = { ...(groupVal || {}), fields: newFields };
          });
        }
      } else if (Array.isArray(section?.fields)) {
        const pairs = detectExtensoPairsInFields(section.fields);
        if (pairs.size === 0) continue;

        pairs.forEach((srcId, extId) => {
          const expected = brlToExtenso(formData[srcId]);
          const current = formData[extId] ?? "";
          if (current !== expected) {
            stage((draft) => {
              draft[extId] = expected;
            });
          }
        });
      }
    }

    if (next) setFormData(next);
  }, [formData, schemaForExtenso, extensoLockedFieldIds, setFormData]);

  /** Repeatable targets that are reachable in the schema and currently visible */
  const customerImportTargetsFiltered = useMemo(() => {
    if (!Array.isArray(customerImport?.targets)) return [];
    return customerImport.targets.filter((t) => {
      const repeatableSection = schemaForVisibility?.find(
        (s) => s.groupKey === t.groupKey && s.repeatable
      );
      if (!repeatableSection) return true;
      return isSchemaSectionShown(repeatableSection, formData);
    });
  }, [customerImport?.targets, formData, schemaForVisibility]);

  // ---------------------------------------------------------------------------
  // Section open/close state
  // ---------------------------------------------------------------------------
  const [openSections, setOpenSections] = useState(() =>
    normalizedSections.reduce((acc, section) => {
      acc[section.sectionId] = true;
      return acc;
    }, {})
  );

  const [customerImportModalOpen, setCustomerImportModalOpen] = useState(false);

  const importTargetsReachable =
    customerImportTargetsFiltered.length > 0 &&
    normalizedSections.some((section) =>
      customerImportTargetsFiltered.some((t) => t.groupKey === section.groupKey)
    );

  const showCustomerImport =
    customerImport?.enabled &&
    importTargetsReachable &&
    typeof onAppendRepeatableRows === "function";

  // ---------------------------------------------------------------------------
  // Auto-expand sections with errors
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!errors) return;

    const next = { ...openSections };

    visibleSections.forEach((section) => {
      if (section.repeatable) {
        const group = formData[section.groupKey] || [];
        const hasError = group.some((item) =>
          section.fields.some(
            (field) =>
              isRepeatableFieldVisible(field, item) && errors?.[field.id]
          )
        );
        if (hasError) next[section.sectionId] = true;
      } else if (section.type === "conditional") {
        const selected = formData[section.groupKey];
        if (!selected?.type) return;

        const hasError = section.options[selected.type]?.some(
          (field) => errors?.[field.id]
        );
        if (hasError) next[section.sectionId] = true;
      } else {
        const hasError = section.fields?.some(
          (field) => errors?.[field.id]
        );
        if (hasError) next[section.sectionId] = true;
      }
    });

    setOpenSections(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  // ---------------------------------------------------------------------------
  // Scalar change
  // ---------------------------------------------------------------------------
  const handleScalarChange = (fieldId, value, section) => {
    setFormData((prev) => {
      const next = { ...prev, [fieldId]: value };

      if (section?.fields) {
        section.fields.forEach((f) => {
          if (f.maskDependsOn === fieldId) {
            next[f.id] = "";
          }
          if (f.default != null && f.id === fieldId && value === "") {
            next[f.id] = f.default;
          }
        });
      }

      return next;
    });
  };

  // ---------------------------------------------------------------------------
  // Repeatable logic
  // ---------------------------------------------------------------------------
  const canAddRepeatableItem = (section) => {
    const max = section.maxItems;
    if (max == null) return true;
    return (formData[section.groupKey] || []).length < max;
  };

  const canRemoveRepeatableItem = (section) => {
    const min = section.minItems ?? 0;
    return (formData[section.groupKey] || []).length > min;
  };

  const addRepeatableItem = (section) => {
    if (!canAddRepeatableItem(section)) return;

    const emptyObject = {};
    section.fields.forEach((field) => {
      emptyObject[field.id] = "";
    });

    setFormData((prev) => ({
      ...prev,
      [section.groupKey]: [
        ...(prev[section.groupKey] || []),
        emptyObject,
      ],
    }));
    onFieldFocus?.(section.groupKey);
  };

  const removeRepeatableItem = (section, index) => {
    if (!canRemoveRepeatableItem(section)) return;

    const updated = [...(formData[section.groupKey] || [])];
    updated.splice(index, 1);

    setFormData((prev) => ({
      ...prev,
      [section.groupKey]: updated,
    }));
  };

  const handleRepeatableChange = (
    groupKey,
    index,
    fieldId,
    value,
    section
  ) => {
    const updated = [...(formData[groupKey] || [])];
    const row = { ...updated[index], [fieldId]: value };

    if (
      fieldId === "ESTADO_CIVIL_PESSOA_FISICA" &&
      value !== "casado" &&
      section?.fields
    ) {
      section.fields.forEach((f) => {
        if (
          f.showWhen?.field === "ESTADO_CIVIL_PESSOA_FISICA" &&
          f.showWhen?.value === "casado"
        ) {
          row[f.id] = "";
        }
      });
    }

    updated[index] = row;

    setFormData((prev) => ({
      ...prev,
      [groupKey]: updated,
    }));
  };

  const renderRepeatableField = (field, item, groupKey, index, section) => {
    const value = item[field.id] ?? "";
    const resolvedField = withResolvedMask(field, item);
    const nm = numericMaskProps(resolvedField, item);
    const isExtensoLocked = extensoLockedFieldIds.has(field.id);
    const extProps = extensoInputProps(isExtensoLocked, field.description);

    switch (field.type) {
      case "text":
      case "email":
      case "tel":
      case "number":
        return (
          <Input
            key={field.id}
            label={field.label}
            type={field.type}
            required={field.required}
            min={field.min}
            max={field.max}
            step={field.step}
            inputMode={nm.inputMode}
            autoComplete={nm.autoComplete}
            value={
              resolvedField.mask
                ? maskDisplayValue(resolvedField, value, item)
                : value
            }
            onFocus={() => onFieldFocus?.(groupKey)}
            onBlur={() => onFieldBlur?.()}
            className={cn(isExtensoLocked && EXTENSO_LOCKED_INPUT_CLASS)}
            {...extProps}
            onChange={(e) =>
              handleRepeatableChange(
                groupKey,
                index,
                field.id,
                resolvedField.mask
                  ? normalizeMaskedInput(resolvedField, e.target.value, item)
                  : e.target.value,
                section
              )
            }
          />
        );

      case "date":
        return (
          <Input
            key={field.id}
            label={field.label}
            type="date"
            required={field.required}
            value={value}
            onFocus={() => onFieldFocus?.(groupKey)}
            onBlur={() => onFieldBlur?.()}
            onChange={(e) =>
              handleRepeatableChange(
                groupKey,
                index,
                field.id,
                e.target.value,
                section
              )
            }
          />
        );

      case "select":
        return (
          <Select
            key={field.id}
            label={field.label}
            required={field.required}
            options={field.options}
            placeholder={field.placeholder}
            value={value}
            onFocus={() => onFieldFocus?.(groupKey)}
            onBlur={() => onFieldBlur?.()}
            onChange={(v) =>
              handleRepeatableChange(groupKey, index, field.id, v, section)
            }
          />
        );

      default:
        return (
          <Input
            key={field.id}
            label={field.label}
            type="text"
            required={field.required}
            inputMode={nm.inputMode}
            autoComplete={nm.autoComplete}
            value={
              resolvedField.mask
                ? maskDisplayValue(resolvedField, value, item)
                : value
            }
            onFocus={() => onFieldFocus?.(groupKey)}
            onBlur={() => onFieldBlur?.()}
            className={cn(isExtensoLocked && EXTENSO_LOCKED_INPUT_CLASS)}
            {...extProps}
            onChange={(e) =>
              handleRepeatableChange(
                groupKey,
                index,
                field.id,
                resolvedField.mask
                  ? normalizeMaskedInput(resolvedField, e.target.value, item)
                  : e.target.value,
                section
              )
            }
          />
        );
    }
  };

  // ---------------------------------------------------------------------------
  // Conditional logic
  // ---------------------------------------------------------------------------
  /** Resolve option key case-insensitively so "Casa" and "casa" both match schema key "casa". */
  const getConditionalOptionKey = (section, value) => {
    if (!value || !section.options) return value;
    const key = Object.keys(section.options).find(
      (k) => String(k).toLowerCase() === String(value).toLowerCase()
    );
    return key ?? value;
  };

  const handleConditionalTypeChange = (section, type) => {
    const canonicalKey = getConditionalOptionKey(section, type);
    setFormData((prev) => {
      const next = {
        ...prev,
        [section.groupKey]: {
          type: canonicalKey,
          fields: {},
        },
      };
      if (
        section.groupKey === "clausula_garantia" &&
        String(canonicalKey).toLowerCase() !== "fiador"
      ) {
        next.fiadores = [];
      }
      return next;
    });
  };

  const handleConditionalFieldChange = (
    groupKey,
    fieldId,
    value
  ) => {
    setFormData((prev) => ({
      ...prev,
      [groupKey]: {
        ...prev[groupKey],
        fields: {
          ...prev[groupKey]?.fields,
          [fieldId]: value,
        },
      },
    }));
  };

  // ---------------------------------------------------------------------------
  // Field renderer (scalar)
  // ---------------------------------------------------------------------------
  const renderScalarField = (field, section) => {
    const isExtensoLocked = extensoLockedFieldIds.has(field.id);
    const resolvedField = withResolvedMask(field, formData);
    const commonProps = {
      label: field.label,
      description: field.description,
      required: field.required,
      error: errors?.[field.id],
      className: "mb-4",
      onFocus: () => onFieldFocus?.(field.id),
      onBlur: () => onFieldBlur?.(),
    };

    switch (field.type) {
      case "text":
      case "email":
      case "tel":
      case "number": {
        const raw = formData[field.id] ?? "";
        const nm = numericMaskProps(resolvedField, formData);
        const extProps = extensoInputProps(isExtensoLocked, field.description);
        const dynamicLabel =
          field.maskDependsOn && field.maskByValue
            ? formData[field.maskDependsOn] === "cnpj"
              ? field.label.replace(/^CPF\/CNPJ/i, "CNPJ")
              : formData[field.maskDependsOn] === "cpf"
                ? field.label.replace(/^CPF\/CNPJ/i, "CPF")
                : field.label
            : field.label;
        return (
          <Input
            key={field.id}
            {...commonProps}
            {...extProps}
            label={dynamicLabel}
            type={field.type}
            placeholder={field.placeholder}
            min={field.min}
            max={field.max}
            step={field.step}
            inputMode={nm.inputMode}
            autoComplete={nm.autoComplete}
            value={
              resolvedField.mask
                ? maskDisplayValue(resolvedField, raw, formData)
                : raw
            }
            className={cn(
              "mb-4",
              isExtensoLocked && EXTENSO_LOCKED_INPUT_CLASS
            )}
            onChange={(e) =>
              handleScalarChange(
                field.id,
                resolvedField.mask
                  ? normalizeMaskedInput(resolvedField, e.target.value, formData)
                  : e.target.value,
                section
              )
            }
          />
        );
      }

      case "date":
        return (
          <Input
            key={field.id}
            {...commonProps}
            type="date"
            value={formData[field.id] || ""}
            onChange={(e) =>
              handleScalarChange(field.id, e.target.value, section)
            }
          />
        );

      case "select":
        return (
          <Select
            key={field.id}
            {...commonProps}
            options={field.options}
            placeholder={field.placeholder}
            value={formData[field.id] || ""}
            onChange={(value) =>
              handleScalarChange(field.id, value, section)
            }
          />
        );

      case "radio": {
        const selected =
          formData[field.id] ?? field.default ?? field.options?.[0]?.value ?? "";
        return (
          <div key={field.id} className="mb-4 space-y-2">
            <p className="text-sm font-medium text-foreground">
              {field.label}
              {field.required && (
                <span className="text-destructive ml-1">*</span>
              )}
            </p>
            <div className="flex flex-wrap gap-4">
              {(field.options || []).map((opt) => (
                <label
                  key={opt.value}
                  className="inline-flex items-center gap-2 text-sm cursor-pointer"
                >
                  <Input
                    type="radio"
                    name={field.id}
                    checked={selected === opt.value}
                    onChange={() =>
                      handleScalarChange(field.id, opt.value, section)
                    }
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            {errors?.[field.id] && (
              <p className="text-sm text-destructive">{errors[field.id]}</p>
            )}
          </div>
        );
      }

      case "checkbox":
        return (
          <Checkbox
            key={field.id}
            {...commonProps}
            checked={!!formData[field.id]}
            onChange={(e) =>
              handleScalarChange(field.id, e.target.checked, section)
            }
          />
        );

      default:
        return null;
    }
  };

  const renderSectionBody = (section) => (
    <>
      {section.repeatable && (
        <div className="bg-nested rounded-lg p-4 space-y-4 border border-primary/10">
          {(formData[section.groupKey] || []).map((item, index) => (
            <div
              key={index}
              className="border border-primary/15 bg-muted p-4 rounded relative space-y-3"
            >
              {canRemoveRepeatableItem(section) && (
                <button
                  type="button"
                  onClick={() => removeRepeatableItem(section, index)}
                  className="absolute top-2 right-2 text-red-500 text-sm"
                >
                  Remover
                </button>
              )}

              {section.fields
                .filter((field) => isRepeatableFieldVisible(field, item))
                .map((field) =>
                  renderRepeatableField(
                    field,
                    item,
                    section.groupKey,
                    index,
                    section
                  )
                )}
            </div>
          ))}

          {canAddRepeatableItem(section) && (
            <button
              type="button"
              onClick={() => addRepeatableItem(section)}
              className="px-4 py-2 bg-primary text-white rounded text-sm"
            >
              + Adicionar
            </button>
          )}
        </div>
      )}

      {section.type === "conditional" && (
        <>
          <Select
            label="Selecione"
            required={section.requireTypeSelection}
            options={Object.keys(section.options || {}).map((key) => ({
              label: getConditionalOptionLabel(section, key),
              value: key,
            }))}
            value={formData[section.groupKey]?.type ?? ""}
            onFocus={() => onFieldFocus?.(section.groupKey)}
            onBlur={() => onFieldBlur?.()}
            onChange={(value) => {
              handleConditionalTypeChange(section, value);
              onFieldFocus?.(section.groupKey);
            }}
          />

          {(() => {
            const selectedType = formData[section.groupKey]?.type;
            const optionsKey = selectedType
              ? getConditionalOptionKey(section, selectedType)
              : null;
            const fieldsToRender = optionsKey
              ? section.options?.[optionsKey]
              : null;
            if (!fieldsToRender || fieldsToRender.length === 0) return null;
            return (
              <div className="bg-nested rounded-lg p-4 space-y-4 border border-primary/10">
                {fieldsToRender.map((field) => {
                  const raw =
                    formData[section.groupKey]?.fields?.[field.id] ?? "";

                  if (field.type === "date") {
                    return (
                      <Input
                        key={field.id}
                        label={field.label}
                        type="date"
                        required={field.required}
                        value={raw}
                        onFocus={() => onFieldFocus?.(section.groupKey)}
                        onBlur={() => onFieldBlur?.()}
                        onChange={(e) =>
                          handleConditionalFieldChange(
                            section.groupKey,
                            field.id,
                            e.target.value
                          )
                        }
                      />
                    );
                  }

                  const nm = numericMaskProps(field);
                  const isExtensoLocked = extensoLockedFieldIds.has(field.id);
                  const extProps = extensoInputProps(
                    isExtensoLocked,
                    field.description
                  );
                  return (
                    <Input
                      key={field.id}
                      label={field.label}
                      type={field.type === "number" ? "number" : "text"}
                      required={field.required}
                      placeholder={field.placeholder}
                      className={cn(
                        isExtensoLocked && EXTENSO_LOCKED_INPUT_CLASS
                      )}
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      inputMode={nm.inputMode}
                      autoComplete={nm.autoComplete}
                      value={field.mask ? maskDisplayValue(field, raw) : raw}
                      onFocus={() => onFieldFocus?.(section.groupKey)}
                      onBlur={() => onFieldBlur?.()}
                      {...extProps}
                      onChange={(e) =>
                        handleConditionalFieldChange(
                          section.groupKey,
                          field.id,
                          field.mask
                            ? normalizeMaskedInput(field, e.target.value)
                            : e.target.value
                        )
                      }
                    />
                  );
                })}
              </div>
            );
          })()}
        </>
      )}

      {!section.repeatable &&
        section.type !== "conditional" &&
        section.fields?.map((field) => renderScalarField(field, section))}
    </>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (!normalizedSections.length) {
    return (
      <div className="text-center py-10">
        Selecione um Documento
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showCustomerImport && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="gap-2 w-full sm:w-auto"
              onClick={() => setCustomerImportModalOpen(true)}
            >
              <Icon name="UserPlus" size={18} />
              Escolher clientes
            </Button>
          </div>

          <AddCustomersFromListModal
            isOpen={customerImportModalOpen}
            onClose={() => setCustomerImportModalOpen(false)}
            targets={customerImportTargetsFiltered}
            formSchema={schemaForCustomerModal}
            onConfirm={(groupKey, rows) => {
              onAppendRepeatableRows(groupKey, rows);
            }}
          />
        </>
      )}

      {visibleSections.map((section) => {
        if (layoutMode === "stepper") {
          return (
            <div
              key={section.sectionId}
              className="border border-border rounded-lg p-4 space-y-4"
            >
              <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">
                {section.sectionLabel}
              </h3>
              <div className="space-y-4">{renderSectionBody(section)}</div>
            </div>
          );
        }

        const isOpen = openSections[section.sectionId];

        return (
          <div
            key={section.sectionId}
            className="border border-border rounded-lg"
          >
            <button
              type="button"
              onClick={() =>
                setOpenSections((prev) => ({
                  ...prev,
                  [section.sectionId]: !prev[section.sectionId],
                }))
              }
              className="w-full flex items-center justify-between px-4 py-3 bg-muted/40"
            >
              <span className="font-semibold">{section.sectionLabel}</span>
              <span>{isOpen ? "▲" : "▼"}</span>
            </button>

            {isOpen && (
              <div className="p-4 space-y-4">{renderSectionBody(section)}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DynamicContractForm;